<template>
  <div class="relative" style="width: 100%">
    <div class="group">
      <t-label v-if="label" :for="uuid" :disabled="disabled">{{
        label
      }}</t-label>
      <div
        class="inline-flex w-full overflow-hidden rounded-md border border-solid border-transparent bg-gray-600 transition duration-300 focus-within:outline focus-within:outline-2 focus-within:outline-accent-1 group-focus-within:bg-gray-700 group-hover:border-gray-400"
        :class="{
          '!border-transparent !bg-gray-600 !text-gray-200': disabled,
        }"
      >
        <div
          class="box-border flex w-full max-w-[calc(100%-32px)] px-2.5 font-inter text-xs font-medium leading-[30px] text-gray-50 group-focus-within:text-gray-10"
        >
          <input
            v-if="modelValue.length <= 1"
            :id="uuid"
            ref="thisInput"
            :value="modelValue[0]"
            data-testid="t-input"
            :placeholder="placeholder"
            :type="type"
            class="w-full border-none bg-gray-600 p-0 font-inter text-xs font-medium leading-[30px] text-gray-50 outline-none transition duration-300 placeholder:text-gray-100 focus:!visible disabled:!cursor-not-allowed disabled:bg-gray-600 disabled:text-gray-200 group-focus-within:bg-gray-700 group-focus-within:text-gray-10"
            :disabled="disabled"
            @keydown.enter="$emit('enter-pressed')"
            @keydown.,="moreAddresses"
            @input="editFirstAddress($event)"
          />
          <div
            v-if="modelValue.length > 1"
            class="flex w-full items-center"
            data-testid="t-select-text"
            @click="openOptions"
          >
            <div
              class="max-w-[calc(100%-60px)] overflow-hidden"
              data-testid="t-select-text-first-option"
              @click="fosucFirstAddress"
            >
              {{ modelValue[0].slice(0, 18) }}
            </div>
            <div>
              {{ modelValue[0].length > 18 ? "..." : "" }},&nbsp;+{{
                modelValue.length - 1
              }}&nbsp;{{
                modelValue.length - 1 <= 4
                  ? $t("layout.comments.more_1_4")
                  : $t("layout.comments.more_other")
              }}
            </div>
          </div>
        </div>
        <button
          class="grid h-[32px] w-[32px] cursor-pointer items-center justify-center bg-transparent text-gray-100 transition duration-300 hover:text-gray-50 disabled:!cursor-not-allowed disabled:text-gray-300"
          data-testid="t-open-options"
          :disabled="disabled"
          @click="openOptions"
        >
          <font-awesome-icon :icon="['fas', 'plus']" />
        </button>
      </div>
    </div>
    <div
      ref="optionsBox"
      data-testid="t-options-box"
      class="absolute mt-2 box-border space-y-1 rounded-md border border-solid border-gray-500 bg-gray-700 p-2"
      :class="{
        invisible: !optionsOpened,
      }"
      :style="{ width: optionsWidth }"
    >
      <div v-for="index in 5" :key="index" data-testid="t-option">
        <div class="flex items-center gap-1">
          <div class="relative h-[20px]">
            <input
              :id="uuid"
              v-model="modelValue"
              :value="localOptions[index - 1]"
              class="focus-outline m-0 h-[20px] w-[20px] cursor-pointer appearance-none rounded-md bg-gray-600 transition duration-300 checked:!bg-accent-1 checked:!outline-none hover:bg-gray-400 focus:bg-gray-600 focus:outline-2 focus:outline-accent-1 disabled:!cursor-not-allowed disabled:!bg-gray-600"
              :disabled="!localOptions[index - 1]"
              type="checkbox"
            />
            <div v-show="modelValue.includes(localOptions[index - 1])">
              <Pipe
                class="pointer-events-none absolute left-0 top-0 text-white"
                :class="{
                  '!text-gray-200': disabled,
                }"
              />
            </div>
          </div>
          <div
            data-testid="t-option-input"
            class="group/optionInput box-border flex w-full items-center gap-x-2 overflow-hidden rounded-md border border-solid border-transparent bg-gray-600 pl-2.5 pr-1 transition duration-300 focus-within:outline focus-within:outline-2 focus-within:outline-accent-1 hover:border-gray-400 group-focus-within/optionInput:bg-gray-700"
            :class="{
              '!border-transparent !bg-gray-600 !text-gray-200': disabled,
              '!outline !outline-1 !outline-destructive-2 focus:!outline-destructive-2':
                type === 'email' &&
                localOptions[index - 1] &&
                !validateEmail(localOptions[index - 1]),
            }"
          >
            <input
              :ref="
                (el: any) => {
                  refOptionInputs[index - 1] = el;
                }
              "
              :value="localOptions[index - 1]"
              :placeholder="optionsPlaceholder"
              :type="type"
              class="w-full border-none bg-gray-600 p-0 font-inter text-xs font-medium leading-[30px] text-gray-50 outline-none transition duration-300 placeholder:text-gray-100 focus:text-gray-10 disabled:!cursor-not-allowed disabled:bg-gray-600 disabled:text-gray-200"
              :disabled="
                disabled ||
                (!localOptions[index - 1] && index - 1 > localOptions.length)
              "
              @input="updateOption($event, index - 1)"
              @blur="onBlurOption()"
            />
            <button
              v-if="localOptions[index - 1]"
              class="grid !h-6 !w-6 flex-[0_0_24px] cursor-pointer items-center justify-center rounded-md bg-gray-400 text-gray-100 transition duration-300 hover:bg-destructive-1 hover:text-white disabled:!cursor-not-allowed disabled:text-gray-300"
              :disabled="disabled"
              @click="deleteOption(index - 1)"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.4688 4.9375C15.8242 4.9375 16.125 5.23828 16.125 5.59375C16.125 5.97656 15.8242 6.25 15.4688 6.25H15.1406L14.4844 15.1367C14.4023 16.0664 13.6641 16.75 12.7344 16.75H7.23828C6.30859 16.75 5.57031 16.0664 5.48828 15.1367L4.83203 6.25H4.53125C4.14844 6.25 3.875 5.97656 3.875 5.59375C3.875 5.23828 4.14844 4.9375 4.53125 4.9375H6.41797L7.42969 3.43359C7.70312 3.02344 8.19531 2.75 8.71484 2.75H11.2578C11.7773 2.75 12.2695 3.02344 12.543 3.43359L13.5547 4.9375H15.4688ZM8.71484 4.0625C8.63281 4.0625 8.55078 4.11719 8.52344 4.17188L8.00391 4.9375H11.9688L11.4492 4.17188C11.4219 4.11719 11.3398 4.0625 11.2578 4.0625H8.71484ZM13.8281 6.25H6.14453L6.80078 15.0547C6.82812 15.2734 7.01953 15.4375 7.23828 15.4375H12.7344C12.9531 15.4375 13.1445 15.2734 13.1719 15.0547L13.8281 6.25Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>
        <div
          v-if="
            type === 'email' &&
            localOptions[index - 1] &&
            !validateEmail(localOptions[index - 1]) &&
            errorText !== ''
          "
          class="font-regular ml-6 mt-1 flex items-start gap-[5px] font-inter text-xs leading-[16px] text-destructive-2"
          data-testid="t-error-text"
        >
          <div class="h-5 w-5">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 2.75C13.8555 2.75 17 5.89453 17 9.75C17 13.6328 13.8555 16.75 10 16.75C6.11719 16.75 3 13.6328 3 9.75C3 5.89453 6.11719 2.75 10 2.75ZM10 15.4375C13.1172 15.4375 15.6875 12.8945 15.6875 9.75C15.6875 6.63281 13.1172 4.0625 10 4.0625C6.85547 4.0625 4.3125 6.63281 4.3125 9.75C4.3125 12.8945 6.85547 15.4375 10 15.4375ZM10 11.0625C9.61719 11.0625 9.34375 10.7891 9.34375 10.4062V6.90625C9.34375 6.55078 9.61719 6.25 10 6.25C10.3555 6.25 10.6562 6.55078 10.6562 6.90625V10.4062C10.6562 10.7891 10.3555 11.0625 10 11.0625ZM10 11.9922C10.4648 11.9922 10.8477 12.375 10.8477 12.8398C10.8477 13.3047 10.4648 13.6875 10 13.6875C9.50781 13.6875 9.125 13.3047 9.125 12.8398C9.125 12.375 9.50781 11.9922 10 11.9922Z"
                fill="currentColor"
              />
            </svg>
          </div>
          <span class="inline-flex min-h-[20px] items-center">{{
            errorText
          }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import validateEmail from "@/utils/validateEmail";
  import { Pipe } from "@topol/icons";
  import { TLabel } from "@topol/twig-ui";
  import { v4 as uuidv4 } from "uuid";
  import { onClickOutside } from "@vueuse/core";
  import { ref, onMounted, onBeforeUnmount } from "vue";

  interface Props {
    options: string[];
    placeholder?: string;
    disabled?: boolean;
    label?: string;
    optionsWidth?: string;
    optionsPlaceholder?: string;
    type?: string;
    errorText?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    placeholder: "",
    disabled: false,
    label: "",
    optionsWidth: "100%",
    optionsPlaceholder: "",
    type: "text",
    errorText: "",
  });

  const emit = defineEmits([
    "update:modelValue",
    "enter-pressed",
    "update-options",
  ]);

  const modelValue = defineModel<string[]>({ required: true });
  const uuid = uuidv4();
  const optionsOpened = ref(false);
  const thisInput = ref();
  const optionsBox = ref();
  const offset = ref(0);
  const refOptionInputs = ref<any>([]);
  const localOptions = ref(JSON.parse(JSON.stringify(props.options)));

  function openOptions() {
    optionsOpened.value = true;
    if (
      modelValue.value[0] &&
      !localOptions.value.includes(modelValue.value[0])
    ) {
      localOptions.value.unshift(modelValue.value[0]);
      if (props.options.length < 5) {
        emit("update-options", localOptions.value);
      } else {
        offset.value = 1;
      }
    }
  }

  onClickOutside(optionsBox, () => {
    closeOptions();
  });

  function updateOption(event: any, index: number) {
    if (localOptions.value[index] === event.target.value) return;
    if (modelValue.value.includes(localOptions.value[index])) {
      modelValue.value[modelValue.value.indexOf(localOptions.value[index])] =
        event.target.value;
    }
    localOptions.value[index] = event.target.value;
  }

  function onBlurOption() {
    if (
      JSON.stringify(
        localOptions.value.slice(offset.value, localOptions.value.length)
      ) === JSON.stringify(props.options)
    ) {
      return;
    }
    emit(
      "update-options",
      localOptions.value.slice(offset.value, localOptions.value.length)
    );
  }

  function deleteOption(index: number) {
    if (modelValue.value.includes(localOptions.value[index])) {
      modelValue.value.splice(
        modelValue.value.indexOf(localOptions.value[index]),
        1
      );
    }
    localOptions.value.splice(index, 1);
    if (index === 0 && offset.value > 0) {
      offset.value--;
      return;
    }
    emit(
      "update-options",
      localOptions.value.slice(offset.value, localOptions.value.length)
    );
  }

  function closeOptions() {
    optionsOpened.value = false;
  }

  function editFirstAddress(event: any) {
    if (localOptions.value.includes(modelValue.value[0])) {
      localOptions.value[localOptions.value.indexOf(modelValue.value[0])] =
        event.target.value;
    }
    modelValue.value[0] = event.target.value;
  }

  function moreAddresses(e: any) {
    e.preventDefault();
    openOptions();
    e.target.blur();
  }

  function fosucFirstAddress() {
    optionsOpened.value = true;
    const indexOfFirstOption = localOptions.value.indexOf(modelValue.value[0]);
    setTimeout(() => {
      refOptionInputs.value[indexOfFirstOption].focus();
    }, 10);
  }

  onMounted(() => {
    window.$event.on("iframeClick", closeOptions);
  });

  onBeforeUnmount(() => {
    window.$event.off("iframeClick", closeOptions);
  });
</script>
